import { Box, Button, Container, Stack, Tooltip } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { FormikTouched, setNestedObjectValues, useFormikContext } from 'formik';
import { InformationOutlinedIcon } from '@synergy/theme-next';
import {
    PoolButton,
    PoolConfirmationModal,
    PoolModal,
    PoolModalContent,
    PoolRow,
    PoolTypography,
    useIsMobile,
} from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement, useState } from 'react';
import { styled } from '@mui/material';
import { useNavigate } from 'react-router';

import { OrderDataFormValues } from './shopping-cart-page';
import { PriceSum } from './supplier-table/shared-components/price-sum';
import { ROUTES } from '../../utils/routes';
import { cartInfoSelector } from '../../store/shopping-cart-selectors';
import { refreshAndSubmitInquiry } from '../../store/shopping-cart-actions';
import { useAppDispatch, useAppSelector } from '../../../store/store';

const StyledStack = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    padding: theme.spacing(3, 0),
    gap: theme.spacing(6),

    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column-reverse',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: theme.spacing(2),
        gap: theme.spacing(2),
    },
}));

const Wrapper = styled(Box)(({ theme }) => ({
    position: 'sticky',
    width: '100%',
    bottom: 0,
    zIndex: 999,
    backgroundColor: theme.palette['grey'][100],
}));

export const ShoppingCartFooter = ({ isSubmitting }: { isSubmitting: boolean }): ReactElement => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const { surcharges, deliveryCosts, total, currency } = useAppSelector(cartInfoSelector);
    const formik = useFormikContext<OrderDataFormValues>();

    const [isInquiryDialogOpen, setInquiryDialogOpen] = useState(false);
    const [isInquiryInfoOpen, setIsInquiryInfoOpen] = useState(false);
    const [isInquirySubmitting, setIsInquirySubmitting] = useState(false);

    const handleSubmitInquiry = async (): Promise<void> => {
        setInquiryDialogOpen(false);
        setIsInquirySubmitting(true);

        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            formik.setTouched(setNestedObjectValues<FormikTouched<OrderDataFormValues>>(errors, true));
        } else if (await dispatch(refreshAndSubmitInquiry())) {
            navigate(ROUTES.cart.submitInquiry);
        }
        setIsInquirySubmitting(false);
    };

    return (
        <>
            <PoolConfirmationModal
                open={isInquiryDialogOpen}
                isSuccessDialog={true}
                title={<FormattedMessage id="app.cart.submit.inquiry" />}
                action={<FormattedMessage id="app.cart.submit.inquiry.confirm" />}
                onConfirm={handleSubmitInquiry}
                onCancel={() => setInquiryDialogOpen(false)}
            >
                <PoolFormattedMessage id="app.cart.submit.inquiry.confirmationText" />
            </PoolConfirmationModal>

            {isMobile && (
                <Stack direction="row" width="100%" alignItems="center" gap={1.5} sx={{ p: 2 }}>
                    <InformationOutlinedIcon color="primary" onClick={() => setIsInquiryInfoOpen(true)} />
                    <Button
                        fullWidth={true}
                        variant="outlined"
                        onClick={() => setInquiryDialogOpen(true)}
                        disabled={isSubmitting || isInquirySubmitting}
                    >
                        <FormattedMessage id="app.cart.submit.inquiry" />
                    </Button>

                    <PoolModal open={isInquiryInfoOpen} onClose={() => setIsInquiryInfoOpen(false)} maxWidth="sm">
                        <PoolModalContent sx={{ padding: 2, textAlign: 'left' }}>
                            <Stack gap={0.5} typography="body2">
                                <PoolRow>
                                    <strong>
                                        <FormattedMessage id="app.cart.submit.inquiry" />
                                    </strong>
                                </PoolRow>
                                <FormattedMessage id="app.cart.submit.inquiryText" />
                            </Stack>
                        </PoolModalContent>
                    </PoolModal>
                </Stack>
            )}
            <Wrapper>
                <Container maxWidth="xl">
                    <StyledStack>
                        {!isMobile && (
                            <Stack marginRight="auto" direction="row" alignItems="center" gap={1.5}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setInquiryDialogOpen(true)}
                                    disabled={isSubmitting || isInquirySubmitting}
                                >
                                    <FormattedMessage id="app.cart.submit.inquiry" />
                                </Button>
                                <Tooltip
                                    title={
                                        <Stack gap={0.5} typography="body2">
                                            <PoolRow>
                                                <strong>
                                                    <FormattedMessage id="app.cart.submit.inquiry" />
                                                </strong>
                                            </PoolRow>
                                            <FormattedMessage id="app.cart.submit.inquiryText" />
                                        </Stack>
                                    }
                                    variant="light"
                                    placement="bottom"
                                >
                                    <Box sx={{ width: 20, height: 20 }}>
                                        <InformationOutlinedIcon color="primary" />
                                    </Box>
                                </Tooltip>
                            </Stack>
                        )}

                        <Box width={isMobile ? '100%' : 260}>
                            <PoolButton
                                variant="contained"
                                fullWidth={true}
                                data-testid="submit-cart"
                                onClick={() => formik.handleSubmit()}
                                disabled={isSubmitting || isInquirySubmitting}
                            >
                                <FormattedMessage
                                    id={
                                        !formik.values.hasOrderLimit
                                            ? 'app.cart.submit'
                                            : 'app.cart.exceededOrderInquiry'
                                    }
                                />
                            </PoolButton>
                        </Box>
                        {!isMobile && (
                            <Box paddingRight={2}>
                                <PriceSum
                                    surcharges={surcharges}
                                    deliveryCosts={deliveryCosts}
                                    totalNetPrice={total}
                                    currency={currency}
                                />
                            </Box>
                        )}
                        {isMobile && (
                            <Stack typography="body2" marginLeft="auto" gap={0.75} width="100%">
                                <PoolRow justifyContent="space-between" height={20}>
                                    <FormattedMessage id="app.cart.article.price.surchargesAndDeliveryCosts" />
                                    <Box>
                                        <FormattedNumber
                                            value={surcharges}
                                            currency={currency}
                                            style="currency"
                                            signDisplay="exceptZero"
                                        />
                                    </Box>
                                </PoolRow>

                                <Box component="strong">
                                    <PoolRow justifyContent="space-between" height={20} gap={0}>
                                        <PoolRow gap={0.25}>
                                            <Box sx={{ textTransform: 'uppercase' }}>
                                                <FormattedMessage id="app.cart.article.price.totalPrice" />
                                            </Box>
                                            <PoolTypography
                                                typography="caption"
                                                component="span"
                                                translationId="app.cart.article.price.netto"
                                                color="grey.600"
                                            />
                                        </PoolRow>
                                        <Box>
                                            <FormattedNumber value={total} currency={currency} style="currency" />
                                        </Box>
                                    </PoolRow>
                                </Box>
                            </Stack>
                        )}
                    </StyledStack>
                </Container>
            </Wrapper>
        </>
    );
};
